import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { namespaces } from '../../../i18n/i18n.constants';
import downloadAPI from '../../../services/downloadAPI';
import exportsStyle from './exportsStyle';

export interface ExportSummary {
  id: string;
  type: string;
  fileName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  error?: string;
  createdAt?: string;
}

interface ExportsModalProps {
  open: boolean;
  onClose: () => void;
}

export const ExportsModal = ({ open, onClose }: ExportsModalProps) => {
  const classes = exportsStyle();
  const { t } = useTranslation(namespaces.pages.navbar);
  const [exports, setExports] = useState<ExportSummary[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadExports = useCallback(async () => {
    try {
      const response = await downloadAPI.listExcelExports();
      setExports(response.exports || []);
    } catch {
      setExports([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    loadExports();
    const interval = window.setInterval(loadExports, 5000);
    return () => window.clearInterval(interval);
  }, [loadExports, open]);

  const handleDownload = async (exportJob: ExportSummary) => {
    setDownloadingId(exportJob.id);
    try {
      await downloadAPI.downloadExcelExport(exportJob.id, exportJob.fileName);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (exportJob: ExportSummary) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('exports.deleteConfirm'))) return;

    setDeletingId(exportJob.id);
    try {
      await downloadAPI.deleteExcelExport(exportJob.id);
      await loadExports();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('exports.title')}</DialogTitle>
      <DialogContent>
        {!exports.length && <Typography>{t('exports.empty')}</Typography>}
        <List>
          {exports.map((exportJob) => (
            <ListItem key={exportJob.id} divider>
              <ListItemText
                primary={exportJob.fileName}
                secondary={t(`exports.status.${exportJob.status}`)}
              />
              <div className={classes.row}>
                {(exportJob.status === 'pending' || exportJob.status === 'processing') && (
                  <div className={classes.progress}>
                    <LinearProgress
                      variant="determinate"
                      value={exportJob.progress}
                      aria-label={t('exports.progress')}
                    />
                    <Typography variant="caption">{exportJob.progress}%</Typography>
                  </div>
                )}
                {exportJob.status === 'failed' && (
                  <Typography color="error">{exportJob.error || t('exports.failed')}</Typography>
                )}
                {exportJob.status === 'completed' && (
                  <Button
                    onClick={() => handleDownload(exportJob)}
                    disabled={downloadingId === exportJob.id || deletingId === exportJob.id}
                    aria-label={downloadingId === exportJob.id
                      ? t('exports.downloading')
                      : t('exports.download')}
                  >
                    {downloadingId === exportJob.id ? (
                      <CircularProgress size={18} aria-label={t('exports.downloading')} />
                    ) : t('exports.download')}
                  </Button>
                )}
                <Button
                  onClick={() => handleDelete(exportJob)}
                  disabled={downloadingId === exportJob.id || deletingId === exportJob.id}
                  aria-label={deletingId === exportJob.id ? t('exports.deleting') : t('exports.delete')}
                >
                  {deletingId === exportJob.id ? (
                    <CircularProgress size={18} aria-label={t('exports.deleting')} />
                  ) : t('exports.delete')}
                </Button>
              </div>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('exports.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
